// ============================================================
// Student Service
// ============================================================
// Handles operations for the Student module.

import { studentsApi } from '@/api/students.api';
import { enumApi } from '@/api/enum.api';

const studentService = {
  /**
   * Fetch all classes and sections
   */
  getClassesSections: async () => {
    return await studentsApi.getClassesSections();
  },

  /**
   * Fetch paginated list of students
   */
  getStudents: async (schoolId, page = 1, pageSize = 20, params = {}) => {
    if (!schoolId) throw new Error('School ID is required');
    return await studentsApi.getStudents(schoolId, page, pageSize, params);
  },

  /**
   * Fetch a single student by ID
   */
  getStudentById: async (schoolId, studentId) => {
    if (!schoolId) throw new Error('School ID is required');
    return await studentsApi.getById(schoolId, studentId);
  },

  /**
   * Create a new student admission
   */
  createStudent: async (schoolId, studentData) => {
    if (!schoolId) throw new Error('School ID is required');
    return await studentsApi.createAdmission(schoolId, studentData);
  },

  /**
   * Update an existing student
   */
  updateStudent: async (schoolId, studentId, updateData) => {
    if (!schoolId) throw new Error('School ID is required');
    return await studentsApi.update(schoolId, studentId, updateData);
  },

  /**
   * Update student status
   */
  updateStudentStatus: async (schoolId, studentId, status) => {
    if (!schoolId) throw new Error('School ID is required');
    return await studentsApi.updateStatus(schoolId, studentId, status);
  },

  /**
   * Bulk update student statuses
   */
  bulkUpdateStudentStatus: async (schoolId, studentIds, status) => {
    if (!schoolId) throw new Error('School ID is required');
    return await studentsApi.bulkUpdateStatus(schoolId, studentIds, status);
  },

  /**
   * Fetch student status enums
   */
  getStudentStatusEnums: async () => {
    return await enumApi.getStudentStatuses();
  },

  /**
   * Delete (or deactivate) a student - Not provided in new schema explicitly, but can be added if needed
   */
  deleteStudent: async (schoolId, _studentId) => {
    if (!schoolId) throw new Error('School ID is required');
    throw new Error('Delete operation not supported by current API schema');
  },

  /**
   * Fetch student dashboard statistics
   */
  getDashboardStats: async (schoolId, sessionId) => {
    if (!schoolId) throw new Error('School ID is required');
    return await studentsApi.getDashboardStats(schoolId, sessionId);
  },

  /**
   * Promote students to a new session/class/section
   */
  promoteStudents: async (schoolId, payload) => {
    if (!schoolId) throw new Error('School ID is required');
    return await studentsApi.promote(schoolId, payload);
  },

  /**
   * Fetch auto-generated next roll number
   */
  getNextRollNumber: async (payload) => {
    return await studentsApi.getNextRollNumber(payload);
  },

  /**
   * Check if a manual roll number is available
   */
  checkRollNumber: async (payload) => {
    return await studentsApi.checkRollNumber(payload);
  },

  /**
   * Fetch sibling relationship enums
   */
  getSiblingEnums: async () => {
    return await studentsApi.getSiblingEnums();
  },

  /**
   * Search students for sibling selection
   */
  searchStudents: async (schoolId, query, filters = {}, limit = 20) => {
    return await studentsApi.searchStudents(schoolId, query, filters, limit);
  },

  /**
   * Download student bulk admission import template (.xlsx)
   */
  downloadImportTemplate: async (schoolId) => {
    if (!schoolId) throw new Error('School ID is required');
    return await studentsApi.downloadImportTemplate(schoolId);
  },

  /**
   * Bulk import student admissions via Excel file
   */
  importStudents: async (schoolId, file) => {
    if (!schoolId) throw new Error('School ID is required');
    if (!file) throw new Error('File is required for import');
    return await studentsApi.importStudents(schoolId, file);
  },
};

export default studentService;
